/**
 * ForgotPasswordPage — frontend-only stub. Submits to authStore which
 * mocks the request; on success the user sees a "check your email"
 * confirmation. Real backend wiring is a TODO in authStore.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { useAuthStore } from '@/stores/authStore';

export function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const requestPasswordReset = useAuthStore((s) => s.requestPasswordReset);

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-4 text-center">
        <h2 className="text-lg font-semibold tracking-tight">{t('forgot.sent_title')}</h2>
        <p className="text-sm text-ink-muted">{t('forgot.sent_body')}</p>
        <Link to="/login" className="inline-block text-sm font-medium text-spark hover:underline">
          {t('forgot.back_to_login')}
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="text-center">
        <h2 className="text-lg font-semibold tracking-tight">{t('forgot.heading')}</h2>
        <p className="mt-1 text-sm text-ink-muted">{t('forgot.subheading')}</p>
      </div>

      <div>
        <label htmlFor="forgot-email" className="mb-1 block text-sm font-medium text-ink">
          {t('forgot.email_label')}
        </label>
        <input
          id="forgot-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          className="block w-full rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-spark focus:outline-none"
        />
      </div>

      <Button type="submit" className="w-full" size="lg" disabled={submitting}>
        {t('forgot.submit')}
      </Button>

      <p className="text-center text-sm">
        <Link to="/login" className="font-medium text-spark hover:underline">
          {t('forgot.back_to_login')}
        </Link>
      </p>
    </form>
  );
}
